// Stok Operasyon Fişi (Sayım Fazlası / Sayım Eksiği / Fire) — voucher_type prop ile parametrik.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, ListPageHeader } from '../components/ui';
import { CardHero, CardStatusBadge } from '../components/card';
import DataTable from '../components/DataTable';
import SearchableSelect from '../components/SearchableSelect';
import { fmtAmount } from '../utils/numberFormat';

const fmt = (n) => fmtAmount(n);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '';
const today = () => new Date().toISOString().slice(0, 10);

const TYPE_META = {
  count_surplus: { label: 'Sayım Fazlası', icon: '➕', color: '#16a34a', accountHint: '153 Tic.Mal / 679 Diğer Olağ. Gelir' },
  count_deficit: { label: 'Sayım Eksiği',  icon: '➖', color: '#dc2626', accountHint: '689 Diğer Olağ. Gider / 153 Tic.Mal' },
  waste:         { label: 'Fire',           icon: '🔥', color: '#ea580c', accountHint: '689 Diğer Olağ. Gider / 153 Tic.Mal — VUK fire oranı kontrolü' },
};

const REASON_CODES = {
  count_surplus: [
    { code: 'system_error', label: 'Sistem Hatası' },
    { code: 'unrecorded_in', label: 'Kayda Geçmemiş Giriş' },
    { code: 'other', label: 'Diğer' },
  ],
  count_deficit: [
    { code: 'count_error', label: 'Sayım Hatası' },
    { code: 'theft', label: 'Çalıntı/Kayıp' },
    { code: 'shrinkage', label: 'Sebep Bilinmiyor' },
    { code: 'other', label: 'Diğer' },
  ],
  waste: [
    { code: 'spoilage', label: 'Bozulma/Çürüme' },
    { code: 'expired', label: 'Son Kullanma Geçti' },
    { code: 'damage', label: 'Hasar' },
    { code: 'breakage', label: 'Düşme/Kırılma' },
    { code: 'water_fire', label: 'Su/Yangın Hasarı' },
    { code: 'other', label: 'Diğer' },
  ],
};

const emptyLine = () => ({ stock_id: '', stock_code: '', stock_name: '', quantity: 1, quantity_base: 1,
  unit_id: null, unit_type: 'unit1', units: [], line_notes: '', available: null });

const emptyHeader = () => ({
  voucher_date: today(), warehouse_id: '', reason_code: '',
  is_kkeg: false, description: '', notes: '',
  personnel_id: '', special_code1: '', special_code2: '',
});

export default function StockOpVoucherPage({ voucherType = 'waste', autoOpenId } = {}) {
  const meta = TYPE_META[voucherType] || TYPE_META.waste;
  const reasons = REASON_CODES[voucherType] || [];

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [extraLookups, setExtraLookups] = useState({ personnel: [], specialCodes1: [], specialCodes2: [] });

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('add');
  const [selected, setSelected] = useState(null);
  const [header, setHeader] = useState(emptyHeader());
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/stock-op-vouchers', { params: { search, voucher_type: voucherType } });
      if (r.data.success) setList(r.data.data.items);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search, voucherType]);

  const fetchLookups = useCallback(async () => {
    try {
      const [w, s, x] = await Promise.all([
        api.get('/warehouses'),
        api.get('/stocks', { params: { limit: 500 } }),
        api.get('/stock-op-vouchers/lookups/all').catch(() => null),
      ]);
      if (w.data.success) setWarehouses((w.data.data?.items || w.data.data || []).filter(it => it.is_active));
      if (s.data.success) setStocks((s.data.data.items || []).filter(it => it.is_active !== false));
      if (x?.data?.success) setExtraLookups(x.data.data);
    } catch {}
  }, []);

  useEffect(() => { fetchList(); fetchLookups(); }, [fetchList, fetchLookups]);
  useEffect(() => { if (autoOpenId) openView(autoOpenId); }, [autoOpenId]); // eslint-disable-line

  const openAdd = () => {
    setMode('add'); setSelected(null); setHeader(emptyHeader()); setLines([emptyLine()]); setMsg(null); setShowModal(true);
  };

  const openView = async (id) => {
    try {
      const r = await api.get('/stock-op-vouchers/' + id);
      if (r.data.success) { setMode('view'); setSelected(r.data.data); setShowModal(true); }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Yüklenemedi' }); }
  };

  const updateLine = (idx, key, val) => {
    setLines(prev => {
      const nl = [...prev]; const ln = { ...nl[idx], [key]: val };
      if (key === 'stock_id') {
        const stk = stocks.find(s => s.id === val);
        if (stk) {
          ln.stock_code = stk.code; ln.stock_name = stk.name;
          ln.unit_id = stk.unit_id || null; ln.unit_type = 'unit1';
          ln.units = [];
          if (stk.unit_id)  ln.units.push({ id: stk.unit_id,  code: stk.unit_code,  name: stk.unit_name,  type: 'unit1', ratio: 1 });
          ln.quantity_base = parseFloat(ln.quantity) || 1;
        }
      }
      if (key === 'quantity') {
        const u = (ln.units || []).find(x => x.type === ln.unit_type);
        ln.quantity_base = (parseFloat(val) || 0) * (u?.ratio || 1);
      }
      nl[idx] = ln; return nl;
    });
  };

  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setMsg(null);
    if (!header.voucher_date) return setMsg({ type: 'error', text: 'Tarih zorunlu' });
    if (!header.warehouse_id) return setMsg({ type: 'error', text: 'Depo zorunlu' });
    const validLines = lines.filter(l => l.stock_id && parseFloat(l.quantity) > 0);
    if (validLines.length === 0) return setMsg({ type: 'error', text: 'En az bir geçerli kalem girin' });

    setSaving(true);
    try {
      const r = await api.post('/stock-op-vouchers', {
        voucher_type: voucherType,
        voucher_date: header.voucher_date,
        warehouse_id: header.warehouse_id,
        reason_code: header.reason_code || null,
        is_kkeg: !!header.is_kkeg,
        description: header.description || null,
        notes: header.notes || null,
        personnel_id: header.personnel_id || null,
        special_code1: header.special_code1 || null,
        special_code2: header.special_code2 || null,
        lines: validLines.map(l => ({
          stock_id: l.stock_id,
          quantity: parseFloat(l.quantity),
          quantity_base: parseFloat(l.quantity_base) || parseFloat(l.quantity),
          unit_id: l.unit_id || null,
          unit_type: l.unit_type || 'unit1',
          line_notes: l.line_notes || null,
        })),
      });
      if (r.data.success) {
        setMsg({ type: 'success', text: `${meta.label} kaydedildi: ${r.data.data.voucher_no}` });
        await fetchList();
        setTimeout(() => { setShowModal(false); setMsg(null); }, 1200);
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Bu ${meta.label.toLowerCase()} fişi silinecek. Stok hareketi geri alınacak. Devam edilsin mi?`)) return;
    try { await api.delete('/stock-op-vouchers/' + id); setShowModal(false); fetchList(); }
    catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || e.message }); }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title={meta.label + ' Fişleri'}
        count={list.length} unit="fiş"
        search={search} onSearch={setSearch}
        searchPlaceholder="Fiş no, depo, açıklama ara..."
        onRefresh={fetchList} refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni {meta.label} Fişi</Button>}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey={`stock-op-${voucherType}-list`}
          tableClassName="stocks-table" rowClassName="stocks-table-row"
          loading={loading} data={list} emptyText="Fiş bulunamadı" rowKey="id"
          onRowClick={r => openView(r.id)}
          columns={[
            { id: 'voucher_no', header: 'Belge No', accessor: 'voucher_no', sortable: true, width: 170,
              cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.voucher_no}</span> },
            { id: 'voucher_date', header: 'Tarih', accessor: 'voucher_date', sortable: true, width: 100,
              cell: r => fmtDate(r.voucher_date) },
            { id: 'warehouse', header: 'Depo', accessor: 'warehouse_name', sortable: true, width: 160,
              cell: r => r.warehouse_name },
            { id: 'reason', header: 'Sebep', accessor: 'reason_code', sortable: true, width: 140,
              cell: r => {
                if (!r.reason_code) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
                const rc = reasons.find(x => x.code === r.reason_code);
                return <span style={{ fontSize: '0.78rem' }}>{rc?.label || r.reason_code}</span>;
              } },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: r => (
                <div>
                  <div>{r.description || '-'}</div>
                  {r.source_type && <div style={{ fontSize: '0.7rem', color: '#7c3aed', marginTop: 2 }}>🔗 {r.source_type === 'stock_count' ? 'Sayımdan otomatik' : r.source_type}</div>}
                </div>
              ) },
            { id: 'line_count', header: 'Kalem', accessor: 'line_count', sortable: true, width: 70,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' }, cell: r => r.line_count || 0 },
            { id: 'total_quantity', header: 'Miktar', accessor: 'total_quantity', sortable: true, width: 110,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontFamily: 'monospace' },
              cell: r => fmt(r.total_quantity) },
            { id: 'total_cost', header: 'Maliyet', accessor: 'total_cost', sortable: true, width: 120,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontFamily: 'monospace' },
              cell: r => parseFloat(r.total_cost) > 0
                ? <span style={{ color: meta.color, fontWeight: 600 }}>{fmt(r.total_cost)}</span>
                : <span style={{ color: 'var(--text-tertiary)' }}>—</span> },
          ]}
        />
      </div>

      {showModal && (
        <Modal
          icon={null}
          title={mode === 'add' ? `Yeni ${meta.label} Fişi` : `${meta.label} Fişi`}
          size="xl"
          onClose={() => setShowModal(false)}
          closeOnOverlay={false}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {mode === 'view' && selected && !selected.source_type && (
                  <Button variant="danger" onClick={() => remove(selected.id)}>🗑 Sil</Button>
                )}
                {mode === 'view' && selected && selected.source_type && (
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                    Otomatik üretildi — silmek için kaynak belgeden iptal edin
                  </span>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
                {mode === 'add' && <Button variant="ghost" onClick={() => setShowModal(false)}>Vazgeç</Button>}
                {mode === 'add' && <Button variant="primary" onClick={save} loading={saving}>💾 Kaydet</Button>}
              </div>
            </>
          }
        >
          {mode === 'view' && selected && (
            <CardHero
              icon={meta.icon}
              code={selected.voucher_no}
              title={meta.label}
              subtitle={`${fmtDate(selected.voucher_date)} · ${selected.warehouse_name || ''}`}
              statusBadge={<CardStatusBadge preset="approved" />}
              kpis={[
                { label: 'Kalem', value: (selected.lines || []).length, color: 'info' },
                { label: 'Toplam Miktar', value: fmt((selected.lines || []).reduce((s, l) => s + parseFloat(l.quantity_base || l.quantity || 0), 0)), color: 'neutral' },
                { label: 'Maliyet Etkisi', value: fmt((selected.lines || []).reduce((s, l) => s + parseFloat(l.total_cost || 0), 0)), color: voucherType === 'count_surplus' ? 'success' : 'danger' },
              ]}
            />
          )}

          {msg && (
            <div style={{
              padding: '10px 14px', marginBottom: 12, borderRadius: 8, fontSize: '0.85rem',
              background: msg.type === 'error' ? 'rgba(220,38,38,0.1)' : 'rgba(22,163,74,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : 'var(--success)',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(220,38,38,0.3)' : 'rgba(22,163,74,0.3)'),
            }}>{msg.text}</div>
          )}

          {mode === 'add' ? (
            <AddForm
              header={header} setHeader={setHeader}
              lines={lines} updateLine={updateLine} addLine={addLine} removeLine={removeLine}
              warehouses={warehouses} stocks={stocks}
              reasons={reasons} meta={meta} voucherType={voucherType}
              extraLookups={extraLookups}
            />
          ) : (selected && <ViewForm v={selected} meta={meta} reasons={reasons} />)}
        </Modal>
      )}
    </div>
  );
}

function AddForm({ header, setHeader, lines, updateLine, addLine, removeLine, warehouses, stocks, reasons, meta, voucherType, extraLookups = { personnel: [], specialCodes1: [], specialCodes2: [] } }) {
  const h = (k, v) => setHeader(prev => ({ ...prev, [k]: v }));
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{
        padding: '8px 12px', background: meta.color + '14', borderRadius: 8,
        fontSize: '0.78rem', color: meta.color, border: '1px solid ' + meta.color + '40',
      }}>
        {meta.icon} <strong>{meta.label}</strong> — Muhasebe: {meta.accountHint}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <FormField label="Tarih *">
          <input type="date" value={header.voucher_date} onChange={e => h('voucher_date', e.target.value)} />
        </FormField>
        <FormField label="Depo *">
          <SearchableSelect
            value={header.warehouse_id}
            onChange={v => h('warehouse_id', v)}
            options={warehouses}
            getValue={w => w.id}
            getLabel={w => `${w.code} - ${w.name}`}
            placeholder="Depo seç..."
          />
        </FormField>
        <FormField label="Sebep">
          <select value={header.reason_code || ''} onChange={e => h('reason_code', e.target.value)}>
            <option value="">— Sebep seç —</option>
            {reasons.map(rc => <option key={rc.code} value={rc.code}>{rc.label}</option>)}
          </select>
        </FormField>
        {voucherType === 'waste' && (
          <FormField label="KKEG">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!header.is_kkeg} onChange={e => h('is_kkeg', e.target.checked)} />
              <span style={{ fontSize: '0.82rem' }}>VUK fire oranını aştı (KKEG'e yaz)</span>
            </label>
          </FormField>
        )}
        <FormField label="Personel (ops.)">
          <SearchableSelect
            value={header.personnel_id} onChange={v => h('personnel_id', v)}
            options={extraLookups.personnel || []}
            getValue={p => p.id} getLabel={p => (p.code ? p.code + ' - ' : '') + p.full_name}
            placeholder="Personel seç..." allowClear
          />
        </FormField>
        <FormField label="Özel Kod 1">
          <SearchableSelect
            value={header.special_code1} onChange={v => h('special_code1', v)}
            options={extraLookups.specialCodes1 || []}
            getValue={x => x.code} getLabel={x => x.code + ' - ' + x.name}
            placeholder="Özel Kod 1..." allowClear
          />
        </FormField>
        <FormField label="Özel Kod 2">
          <SearchableSelect
            value={header.special_code2} onChange={v => h('special_code2', v)}
            options={extraLookups.specialCodes2 || []}
            getValue={x => x.code} getLabel={x => x.code + ' - ' + x.name}
            placeholder="Özel Kod 2..." allowClear
          />
        </FormField>
        <div style={{ gridColumn: '1 / -1' }}>
          <FormField label="Açıklama">
            <input value={header.description} onChange={e => h('description', e.target.value)} placeholder="Açıklama..." />
          </FormField>
        </div>
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>Kalemler</h4>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 680 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.72rem', textTransform: 'uppercase' }}>
                <th style={{ width: 28, padding: 4 }}>#</th>
                <th style={{ width: 28, padding: 4 }}></th>
                <th style={{ textAlign: 'left', padding: '6px 4px', minWidth: 280 }}>Stok / Açıklama</th>
                <th style={{ textAlign: 'right', padding: '6px 4px', width: 90 }}>Miktar</th>
                <th style={{ textAlign: 'left', padding: '6px 4px', width: 110 }}>Birim</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, idx) => {
                const is2 = { width: '100%', padding: 4, background: 'var(--bg-input)', border: '1px solid var(--border-input)', borderRadius: 4, color: 'var(--text-primary)', fontSize: '0.8rem' };
                return (
                  <React.Fragment key={idx}>
                    <tr style={{ borderBottom: 'none' }}>
                      <td style={{ padding: 2, textAlign: 'center', verticalAlign: 'top', paddingTop: 14, color: 'var(--text-tertiary)' }}>{idx + 1}</td>
                      <td style={{ padding: 2, verticalAlign: 'top', paddingTop: 10 }}>
                        {lines.length > 1 && (
                          <button onClick={() => removeLine(idx)}
                            style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1rem' }}>×</button>
                        )}
                      </td>
                      <td rowSpan={2} style={{ padding: 4, verticalAlign: 'top', minWidth: 280 }}>
                        <div style={{ padding: '7px 9px', background: 'var(--bg-secondary)',
                                      border: ln.stock_id ? '1px solid var(--border-secondary)' : '1px dashed var(--border-input)',
                                      borderRadius: 8 }}>
                          <SearchableSelect
                            value={ln.stock_id}
                            onChange={v => updateLine(idx, 'stock_id', v)}
                            options={stocks}
                            getValue={s => s.id}
                            getLabel={s => `${s.code} - ${s.name}`}
                            placeholder="Stok seç..."
                          />
                          {ln.stock_id && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                              {ln.stock_code}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: 2, width: 90 }}>
                        <input type="number" step="0.001" value={ln.quantity}
                          onChange={e => updateLine(idx, 'quantity', e.target.value)}
                          style={{ ...is2, textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: 2, width: 110 }}>
                        <select value={ln.unit_type || 'unit1'} onChange={e => updateLine(idx, 'unit_type', e.target.value)} style={is2}>
                          {(ln.units || []).map(u => <option key={u.type} value={u.type}>{u.name} (x{u.ratio})</option>)}
                          {(!ln.units || ln.units.length === 0) && <option value="unit1">Adet</option>}
                        </select>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                      <td colSpan={2}></td>
                      <td colSpan={2} style={{ padding: '2px 4px 8px' }}>
                        <input value={ln.line_notes} onChange={e => updateLine(idx, 'line_notes', e.target.value)}
                          placeholder="Açıklama (opsiyonel)..."
                          style={{ ...is2, fontSize: '0.78rem', background: 'transparent', border: '1px dashed var(--border-input)' }} />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-start' }}>
          <Button size="sm" variant="primary" onClick={addLine}>+ Satır Ekle</Button>
        </div>
      </div>
    </div>
  );
}

function ViewForm({ v, meta, reasons }) {
  const reason = reasons.find(r => r.code === v.reason_code);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 10 }}>
        <Info label="Depo" value={v.warehouse_name || '-'} />
        <Info label="Tarih" value={fmtDate(v.voucher_date)} />
        {reason && <Info label="Sebep" value={reason.label} />}
        {v.is_kkeg && <Info label="KKEG" value={<span style={{ color: 'var(--warning)' }}>⚠ Evet</span>} />}
        {v.personnel_name && <Info label="Personel" value={v.personnel_name} />}
        {v.special_code1 && <Info label="Özel Kod 1" value={v.special_code1} />}
        {v.special_code2 && <Info label="Özel Kod 2" value={v.special_code2} />}
        <Info label="Düzenleyen" value={v.created_by_name || '-'} />
      </div>
      {v.description && <div style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.85rem' }}>
        <strong>Açıklama:</strong> {v.description}
      </div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
            <th style={{ textAlign: 'left', padding: '6px 4px', width: 40 }}>#</th>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>Stok</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', width: 110 }}>Miktar</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', width: 110 }}>Maliyet</th>
            <th style={{ textAlign: 'left', padding: '6px 4px' }}>Açıklama</th>
          </tr>
        </thead>
        <tbody>
          {(v.lines || []).map(l => (
            <tr key={l.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
              <td style={{ padding: 4, color: 'var(--text-tertiary)' }}>{l.line_no}</td>
              <td style={{ padding: 4 }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{l.stock_code}</div>
                <div>{l.stock_name}</div>
              </td>
              <td style={{ padding: 4, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                {fmt(l.quantity)} {l.unit_name || l.unit_code || ''}
              </td>
              <td style={{ padding: 4, textAlign: 'right', fontFamily: 'monospace', color: meta.color }}>
                {l.total_cost > 0 ? fmt(l.total_cost) : '—'}
              </td>
              <td style={{ padding: 4, fontSize: '0.78rem' }}>{l.line_notes || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.88rem' }}>{value}</div>
    </div>
  );
}
